import { useEffect, useRef, useState } from "react";
import SimplePeer from "simple-peer/simplepeer.min.js";
import { SimpleWebSocket } from "simple-websockets";
import CameraLogo from "../../../assets/device-icon.svg";
import UserLogo from "../../../assets/user-icon.svg";
import Footer from "../../layout/Footer/Footer.js";
import Navbar from "../../layout/Navbar/Navbar.js";
import SlideIn from "../../ui/SlideIn/SlideIn.js";
import "./Cameras.scss";

const USE_LOCAL = false;
const API_ADDRESS = USE_LOCAL
	? "http://localhost:1337"
	: "https://gluckmanager.com";
const API_SOCKET = USE_LOCAL ? "ws://localhost:1337" : "wss://gluckmanager.com";

const connections = [];

const Cameras = () => {
	const roomId = new URLSearchParams(window.location.search).get("room");
	const [mediaStream, setMediaStream] = useState(null);
	const videoRef = useRef(null);

	const [previewEnabled, setPreviewEnabled] = useState(false);
	const [availableDevices, setAvailableDevices] = useState([]);
	const [players, setPlayers] = useState([]);
	const [selectedDeviceId, setSelectedDeviceId] = useState("no-device");
	const selectedDevice = availableDevices.find(
		(device) => device.deviceId === selectedDeviceId
	);
	const [selectedPlayer, setSelectedPlayer] = useState("no-player");
	const [connectionStatus, setConnectionStatus] = useState("loading");

	const getPeerConnectionByUUID = (uuid) => {
		return connections.find((connection) => connection.uuid === uuid)
			?.peerConnection;
	};

	const loadPlayers = async () => {
		if (!roomId) return;

		setConnectionStatus("loading");

		const res = await fetch(`${API_ADDRESS}/api/camera/${roomId}`);
		const data = await res.json();
		const { players = [] } = data;

		setPlayers(players);
		setConnectionStatus("available");
	};

	const connect = () => {
		setConnectionStatus("loading");

		const socket = new SimpleWebSocket(API_SOCKET);

		const connectToHUD = (uuid) => {
			console.log("connecting to", uuid);
			const currentPeerConnection = getPeerConnectionByUUID(uuid);
			if (currentPeerConnection) return;

			createConnection(uuid);
		};

		const createConnection = (uuid) => {
			if (!mediaStream) return;

			const existingConnection = connections.find((c) => c.uuid === uuid);
			if (existingConnection?.peerConnection) return;

			const connection = existingConnection || { uuid, peerConnection: null };
			connection.peerConnection = new SimplePeer({
				initiator: true,
				stream: mediaStream,
				trickle: false,
				config: {
					iceServers: [
						{ urls: "stun:stun1.l.google.com:19302" },
						{ urls: "stun:stun.l.google.com:19302" },
						{ urls: "stun:stun2.l.google.com:19302" },
					],
				},
			});

			connection.peerConnection.on("signal", (data) => {
				console.log(data);
				if (data.type !== "offer") return;
				const offerData = JSON.parse(JSON.stringify(data));
				socket.send(
					"offerFromPlayer",
					roomId,
					{ offer: offerData },
					selectedPlayer,
					uuid
				);
			});

			if (!existingConnection) connections.push(connection);

			const cleanup = () => {
				const connection = connections.find((c) => c.uuid === uuid);
				if (connection?.peerConnection) {
					connection.peerConnection.destroy?.();
					connection.peerConnection = null;
				}
			};

			connection.peerConnection.on("close", cleanup);
			connection.peerConnection.on("error", cleanup);

			return connection.peerConnection;
		};

		const closeConnection = (uuid) => {
			const connection = connections.find((c) => c.uuid === uuid);
			connection?.peerConnection?.destroy?.();
		};

		socket.on("badRoom", () => {
			alert("Error! Wrong room!");
			window.location.reload();
		});

		socket.on("offerFromHUD", (_roomId, data, _steamid, uuid) => {
			const peerConnection = getPeerConnectionByUUID(uuid);
			if (peerConnection) {
				peerConnection.signal(data.offer);
			}
		});

		const pingInterval = setInterval(() => {
			socket?.send("ping");
		}, 45000);

		socket.on("hudsOnline", (onlineUUIDs) => {
			const disconnected = connections
				.map(({ uuid }) => uuid)
				.filter((uuid) => !onlineUUIDs.includes(uuid));
			disconnected.forEach((uuid) => {
				closeConnection(uuid);
			});

			const newHUDs = onlineUUIDs.filter(
				(uuid) => !connections.map(({ uuid }) => uuid).includes(uuid)
			);
			newHUDs.forEach((uuid) => {
				connectToHUD(uuid);
			});
		});

		socket.on("connection", () => {
			console.log("CONNECTED");
			setConnectionStatus("connected");
			socket.send("registerAsCamera", roomId, selectedPlayer);
		});

		socket.on("disconnect", () => clearInterval(pingInterval));
		socket.on("error", console.log);
	};

	const requestCameraDevices = async () => {
		try {
			await navigator.mediaDevices.getUserMedia({ video: true });
		} catch (err) {
			console.error("getUserMedia error:", err);
		}

		const devices = (await navigator.mediaDevices.enumerateDevices()).filter(
			(device) => device.deviceId !== ""
		);

		if (
			(!selectedDeviceId || selectedDeviceId === "no-device") &&
			devices.length > 0
		) {
			setSelectedDeviceId(devices[0].deviceId);
		}

		setAvailableDevices(devices);
	};

	const getConnectButtonLabel = () => {
		switch (connectionStatus) {
			case "available":
				return "Connect";
			case "loading":
				return "Connecting...";
			case "connected":
				return "Connected";
			case "error":
				return "Error";
			default:
				return "Connect";
		}
	};

	useEffect(() => {
		if (!selectedDevice) {
			console.log("No selected device. Setting current stream to null");
			return setMediaStream(null);
		}

		navigator.mediaDevices
			.getUserMedia({
				video: {
					width: { ideal: 1920 },
					height: { ideal: 1080 },
					deviceId: { exact: selectedDevice.deviceId },
				},
			})
			.then((stream) => {
				console.log("Setting current stream to", stream);
				setMediaStream(stream);
			})
			.catch((error) => {
				console.error("Error getting user media:", error);
			});
	}, [selectedDevice]);

	useEffect(() => {
		if (mediaStream && videoRef.current) {
			videoRef.current.srcObject = mediaStream;
		} else {
			console.log("No stream or video ref", mediaStream, videoRef.current);
		}
	}, [previewEnabled, mediaStream]);

	useEffect(() => {
		requestCameraDevices();
		loadPlayers();
	}, []);

	const connectButtonDisabled =
		connectionStatus !== "available" ||
		!selectedPlayer ||
		selectedPlayer === "no-player" ||
		!selectedDeviceId ||
		selectedDevice === "no-device" ||
		!roomId;

	return (
		<>
			<section id="camera-page" className="camera-page">
			<Navbar isVisible={true} />
				<div className="container__camera">
					<div className="camera__nav-heading">
						<SlideIn delay={90} threshold={0.3}>
							<p>Get ready to start capturing!</p>
						</SlideIn>
						<SlideIn delay={120}>
							<h1>CONFIGURE YOUR CAMERA</h1>
						</SlideIn>
					</div>
					<div className="camera-preview">
						<div className="camera-ui">
							<SlideIn direction="up" delay={180}>
								<div className="freecam-outer">
									<div className="freecam-inner">
										{availableDevices.length === 0 && (
											<button
												className="preview-button"
												onClick={() => requestCameraDevices()}
											>
												Grant camera permissions
											</button>
										)}
										{availableDevices.length > 0 && !previewEnabled && (
											<button
												className="preview-button"
												onClick={() => setPreviewEnabled(true)}
											>
												ENABLE PREVIEW
											</button>
										)}
										{previewEnabled && (
											<>
												<button
													className="disablePreview-button"
													onClick={() => setPreviewEnabled(false)}
												>
													DISABLE PREVIEW
												</button>
												<video
													id="facecam-preview"
													ref={videoRef}
													playsInline
													autoPlay
													muted
												/>
											</>
										)}
									</div>
								</div>
							</SlideIn>

							<div id="actions">
								<SlideIn direction="up" delay={100}>
									<div className="selector-container">
										<div className="selector-group">
											<div className="selector__image-text">
												<img
													src={CameraLogo}
													className="selector-logo"
													alt="User-logo"
												/>
												<p>Select a device</p>
											</div>
											<select
												value={selectedDevice?.deviceId}
												onChange={(e) => setSelectedDeviceId(e.target.value)}
											>
												<option value="no-device">Select</option>
												{availableDevices.map((device) => (
													<option key={device.deviceId} value={device.deviceId}>
														{device.label}
													</option>
												))}
											</select>
										</div>

										<div className="selector-group">
											<div className="selector__image-text">
												<img
													src={UserLogo}
													className="selector-logo"
													alt="Camera-logo"
												/>
												<p>Select player</p>
											</div>
											<select
												value={selectedPlayer}
												onChange={(e) => setSelectedPlayer(e.target.value)}
											>
												<option value="no-player">Select</option>
												{players.map((p) => (
													<option key={p.steamid} value={p.steamid}>
														{p.label}
													</option>
												))}
											</select>
										</div>

										<button
											className="connect-button"
											onClick={connect}
											disabled={connectButtonDisabled}
										>
											{getConnectButtonLabel()}
										</button>
									</div>
								</SlideIn>
							</div>
						</div>
					</div>
				</div>
				<SlideIn direction="up" delay={50} threshold={0.1}>
					<Footer alwaysVisible={true} />
				</SlideIn>
			</section>
		</>
	);
};

export default Cameras;
